import React, { useEffect, useState } from "react";
import { Button, Modal, TextInputBase } from "@truevault/aquilo";

type CustomPurposeNameModalProps = {
  open: boolean;
  title: string;
  submitLabel: string;
  initialName?: string;
  existingNames: string[];
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
};

export function CustomPurposeNameModal({
  open,
  title,
  submitLabel,
  initialName = "",
  existingNames,
  onClose,
  onSubmit,
}: CustomPurposeNameModalProps) {
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
    }
  }, [open, initialName]);

  if (!open) {
    return null;
  }

  const trimmedName = name.trim();
  const isDuplicate =
    trimmedName.length > 0 &&
    existingNames.some((existing) => existing.toLowerCase() === trimmedName.toLowerCase());

  const handleSubmit = async () => {
    if (!trimmedName || isDuplicate || submitting) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(trimmedName);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      alignText="left"
      closeButton
      showOverlay
      onClose={onClose}
      contentContainerClasses="w-full"
      contentPadding="px-[24px] pt-5 pb-[4px]"
    >
      <div className="flex flex-col gap-[12px]">
        <TextInputBase
          field="customPurposeName"
          label="Purpose name"
          placeholder="Add a processing activity, keep it short and sweet!"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          autoFocus
          disabled={submitting}
        />
        {isDuplicate && (
          <p className="text-red-500 text-[12px]">A purpose already exists with this name.</p>
        )}
        <div className="flex justify-end items-center gap-[12px] pt-[8px]">
          <Button color="tertiary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => void handleSubmit()}
            disabled={!trimmedName || isDuplicate || submitting}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type RemoveCustomPurposeModalProps = {
  open: boolean;
  purposeName: string;
  onClose: () => void;
  onConfirm: () => void;
};

export function RemoveCustomPurposeModal({
  open,
  purposeName,
  onClose,
  onConfirm,
}: RemoveCustomPurposeModalProps) {
  if (!open) {
    return null;
  }

  return (
    <Modal
      title="Remove purpose?"
      alignText="left"
      closeButton
      showOverlay
      onClose={onClose}
      contentContainerClasses="w-full"
      contentPadding="px-[24px] pt-5 pb-[4px]"
      alignButton="right"
      firstButton="Cancel"
      firstButtonColor="tertiary"
      onFirstButtonClick={onClose}
      secondButton="Remove"
      secondButtonColor="warning"
      onSecondButtonClick={onConfirm}
    >
      <p className="text-neutral-700 text-[14px]">
        Are you sure you want to remove <strong>{purposeName}</strong>? This cannot be undone.
      </p>
    </Modal>
  );
}
